import { H } from "@highlight-run/next/client";
import { Box, styled } from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Spinner from "../components/Spinner";
import { setAuthUser } from "../redux/authUserSlice";
import { RootState } from "../redux/store";
import { setDisableUser, setUser } from "../redux/userSlice";
import { signOutUser } from "./authentication";
import { auth, db, firebaseCloudMessaging } from "./firebase";

const StyledBox = styled(Box)`
  height: 100%;
  width: 100%;
`;

const AuthProvider = ({ children }: any) => {
  const dispatch = useDispatch();
  const shouldRun = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useSelector((state: RootState) => state.user);
  const router = useRouter();

  useEffect(() => {
    if (!user.uid) {
      return;
    }

    if (user && user.disabled) {
      signOutUser();
      router.replace("/auth/login");
    }
  }, [user.disabled]);

  useEffect(() => {
    let unSubscribe: () => void;

    if (shouldRun.current) {
      if (router.pathname === "/") {
        console.info("[AuthProvider] homeRedirectToBrowse", {
          path: router.asPath,
        });
        router.push("/browse");
      }
      console.info("[AuthProvider] init", { path: router.asPath });
      shouldRun.current = false;
      onAuthStateChanged(auth, async (user) => {
        user && dispatch(setAuthUser(user));
        if (user) {
          console.info("[AuthProvider] firebaseUserDetected", {
            path: router.asPath,
            uid: user.uid,
          });

          unSubscribe = onSnapshot(doc(db, "users", user.uid), async (doc) => {
            const userData = doc.data();
            if (userData && userData.disabled) {
              dispatch(setDisableUser(true));
              toast.error("You are disabled, connect with admin.", {
                toastId: "youAreDisabled",
              });
              signOutUser();
            } else {
              if (userData && userData.createdAt) {
                userData.uid = user.uid;
                userData.dob = userData.dob?.toDate().toISOString();
                userData.createdAt = userData.createdAt.toDate().toISOString();
                userData.stripe_verification_attempted_at =
                  userData.stripe_verification_attempted_at
                    ? userData.stripe_verification_attempted_at
                        .toDate()
                        .toISOString()
                    : null;
                userData && dispatch(setUser(userData));
                firebaseCloudMessaging.init();

                // Check if this is an admin proxy login
                const isAdminProxy =
                  localStorage.getItem("admin-proxy-login") === "true";

                // Identify user in Highlight
                if (userData) {
                  process.env.NODE_ENV === "production" &&
                    H.identify(userData.email || user.uid, {
                      id: user.uid,
                      email: userData.email,
                      name: userData.name,
                      phone: userData.phone,
                      userType: userData.userType,
                      createdAt: userData.createdAt,
                      isAdminProxy: isAdminProxy,
                    });
                }

                // Set firebase token as a cookie
                try {
                  const authToken = await user.getIdToken();
                  Cookies.set("authToken", authToken, {
                    expires: 30,
                  });
                  console.info("[AuthProvider] tokenSet", {
                    path: router.asPath,
                  });
                  setIsLoading(false);
                } catch (error) {
                  console.error("Error fetching auth token", error);
                }

                setIsLoading(false);
              }
            }
          });
        } else {
          console.info("[AuthProvider] noFirebaseUser", {
            path: router.asPath,
          });
          setIsLoading(false);
        }
      });
    }

    return () => {
      unSubscribe && unSubscribe();
    };
  }, []);

  return (
    <StyledBox>
      {isLoading ? (
        <StyledBox sx={{ display: "grid", placeItems: "center" }}>
          <Spinner />
        </StyledBox>
      ) : (
        <StyledBox>{children}</StyledBox>
      )}
    </StyledBox>
  );
};

export default AuthProvider;
